/**
 * 환경 설정 — 고객사가 자기 화면을 어떻게 보일지 정한다.
 *
 * 서버 설정(저장소·신원 발급자·포트)이 아니다. 그것은 배포가 정하고 콘솔에서 바꾸지 않는다
 * (`System.tsx`). 여기 있는 것은 고객사 관리자가 고치는 값이고, 저장하면 다음 폴링(약 30초)에
 * 화면으로 나간다.
 *
 * **패널마다 따로 저장한다.** 한 번에 저장하면 한 칸의 오류 때문에 다른 칸까지 저장되지 않고,
 * 무엇이 저장됐는지 헷갈린다. 판(`revision`)은 화면 전체가 하나를 공유한다 — 저장하면 서버가
 * 돌려준 새 판으로 바꿔 끼운다. 통합 관제 연결은 설정 값이 아니라 토큰이라 판과 상관없이 따로 돈다.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { KeyRound, Save, Trash2, Upload } from 'lucide-react';
import {
  DEFAULT_HEADER_PLACE,
  HEADER_PLACE_MAX,
  headerPlace,
  TEMPLATE_STARTERS,
  type CaptionOverflow,
  type CaptionPosition,
  type HeaderPlaceMode,
  type TemplatePayload,
} from '@signageone/core';
import type { HubLinkTokenIssued, IdleMode, SignageOneClient, TenantSettings, TenantSettingsPatch } from '../api/client.js';
import { HoursRulesEditor, type DaypartInput } from '../components/HoursEditor.js';
import { brandFromSettings, useBrand } from '../components/brand.js';
import { TemplatePreview } from '../components/TemplatePreview.js';
import { CommandBox, ErrorNote, formatBytes, Loading, Panel, RelTime } from '../components/ui.js';
import { confirmDialog } from '../components/ConfirmDialog.js';
import { IconButton } from '../components/IconButton.js';
import { Disclosure } from '../components/Disclosure.js';
import { HelpTip } from '../components/HelpTip.js';
import { useAction, useAsync } from '../hooks.js';

/** 템플릿 기본 파랑 — 색을 고르지 않았을 때의 주 색. 색 고르기의 처음 값으로만 쓴다. */
const DEFAULT_ACCENT = '#0a84ff';

/** 허용 주소 입력 — 한 줄에 하나. 빈 줄·앞뒤 공백은 버린다. */
export function parseAllowlistText(text: string): string[] {
  const out: string[] = [];
  for (const line of text.split(/\r?\n/)) {
    const value = line.trim();
    if (value && !out.includes(value)) out.push(value);
  }
  return out;
}

export function SettingsScreen({ client }: { client: SignageOneClient }): ReactNode {
  const loaded = useAsync(() => client.settings(), [client]);
  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const { reload: reloadBrand } = useBrand();

  useEffect(() => {
    if (loaded.data) setSettings(loaded.data);
  }, [loaded.data]);

  if (loaded.error) return <ErrorNote message={loaded.error} />;
  if (!settings) return <Loading />;

  /** 서버가 돌려준 설정으로 바꿔 끼운다 — 판이 올랐고, 콘솔 곳곳의 미리보기도 새 모양으로. */
  const accept = (next: TenantSettings): TenantSettings => {
    setSettings(next);
    reloadBrand();
    return next;
  };

  /** 패널이 고친 칸만 보낸다. 판은 화면이 들고 있는 것. */
  const save = async (patch: Omit<TenantSettingsPatch, 'revision'>): Promise<TenantSettings> =>
    accept(await client.saveSettings({ ...patch, revision: settings.revision }));

  return (
    <div className="screen">
      {settings.updated_at ? (
        <p className="hint">마지막 저장 <RelTime iso={settings.updated_at} /></p>
      ) : null}
      <BrandPanel client={client} settings={settings} save={save} accept={accept} onConflict={loaded.reload} />
      <QuietPanel settings={settings} save={save} onConflict={loaded.reload} />
      {settings.caption ? <CaptionPanel settings={settings} save={save} onConflict={loaded.reload} /> : null}
      {/* 옛 서버는 이 값을 모른다 — 패널을 그리지 않는다. */}
      {settings.html ? <HtmlOfflinePanel settings={settings} save={save} onConflict={loaded.reload} /> : null}
      {/* 옛 서버는 이 값을 모른다(undefined) — 패널을 그리지 않는다. */}
      {settings.new_display_group_id !== undefined ? (
        <NewScreenPanel client={client} settings={settings} save={save} onConflict={loaded.reload} />
      ) : null}
      <AllowlistPanel settings={settings} save={save} onConflict={loaded.reload} />
      <HubLinkPanel client={client} />
    </div>
  );
}

type SaveFn = (patch: Omit<TenantSettingsPatch, 'revision'>) => Promise<TenantSettings>;

/** 그사이 남이 저장했으면(409) 다시 읽는다 — 화면이 들고 있는 판이 낡았다. */
function reloadOnConflict<T>(promise: Promise<T>, onConflict: () => void): Promise<T> {
  return promise.catch((error: unknown) => {
    if ((error as { status?: number }).status === 409) onConflict();
    throw error;
  });
}

/** 미리보기에 쓰는 공지 — 머리글이 잘 보이는 템플릿이다. */
const SAMPLE: TemplatePayload = {
  id: 'announcement',
  data: TEMPLATE_STARTERS.announcement,
} as TemplatePayload;

function BrandPanel({
  client,
  settings,
  save,
  accept,
  onConflict,
}: {
  client: SignageOneClient;
  settings: TenantSettings;
  save: SaveFn;
  accept: (next: TenantSettings) => TenantSettings;
  onConflict: () => void;
}): ReactNode {
  const [name, setName] = useState(settings.brand.name);
  const [accent, setAccent] = useState<string | null>(settings.brand.accent);
  const [logoId, setLogoId] = useState<string | null>(settings.brand.logo_asset_id);
  const savedPlace = settings.brand.place ?? DEFAULT_HEADER_PLACE;
  const [placeMode, setPlaceMode] = useState<HeaderPlaceMode>(savedPlace.mode);
  const [placeText, setPlaceText] = useState(savedPlace.text);
  const [saved, setSaved] = useState(false);
  const action = useAction();
  const fontAction = useAction();
  const fileRef = useRef<HTMLInputElement>(null);

  /* 로고 후보 — 콘텐츠 목록의 이미지. 보관된 것은 화면이 받지 못하므로 빼고 고른다. */
  const images = useAsync(() => client.allAssets({ type: 'image', archived: 'false' }), [client]);
  const logo = images.data?.items.find((asset) => asset.id === logoId) ?? null;

  /* 저장 전의 고친 값으로 미리 본다 — 저장한 뒤에야 보이면 고르는 의미가 없다. */
  const draft = useMemo(
    () => ({
      ...brandFromSettings(settings),
      name,
      accent,
      logoUrl: logoId ? (logo?.preview_url ?? settings.brand.logo_preview_url) : null,
      place: { mode: placeMode, text: placeText },
    }),
    [settings, name, accent, logoId, logo, placeMode, placeText],
  );

  const place = { mode: placeMode, text: placeMode === 'custom' ? placeText.trim() : '' };
  const dirty =
    name.trim() !== settings.brand.name ||
    accent !== settings.brand.accent ||
    logoId !== settings.brand.logo_asset_id ||
    place.mode !== savedPlace.mode ||
    place.text !== savedPlace.text;
  // 직접 입력인데 비었으면 저장하지 않는다 — 서버도 거절한다.
  const placeMissing = placeMode === 'custom' && !place.text;

  const submit = (): void => {
    setSaved(false);
    void action.run(
      () =>
        reloadOnConflict(
          save({
            brand: {
              name: name.trim(),
              accent,
              logo_asset_id: logoId,
              // 옛 서버는 이 칸을 모른다(거절한다) — 고치지 않았으면 보내지 않는다.
              ...(place.mode !== savedPlace.mode || place.text !== savedPlace.text ? { place } : {}),
            },
          }),
          onConflict,
        ),
      (next) => {
        setName(next.brand.name);
        const nextPlace = next.brand.place ?? DEFAULT_HEADER_PLACE;
        setPlaceMode(nextPlace.mode);
        setPlaceText(nextPlace.text);
        setSaved(true);
      },
    );
  };

  const uploadFont = (file: File): void => {
    void fontAction.run(() => client.uploadBrandFont(file), accept);
  };

  return (
    <Panel
      title="브랜드"
      subtitle="템플릿 머리글의 조직 이름·로고, 주 색, 사내 글꼴을 정합니다."
      help="저장하면 다음 템플릿부터 화면에 반영됩니다(약 30초). 이미지·영상 콘텐츠는 그대로입니다. 판정 색(긴급 주황, 증감 초록·빨강)은 바뀌지 않습니다."
    >
      <div className="grid gap-5 p-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        <div className="space-y-4">
          {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
          <label className="field">
            <span>조직 이름</span>
            <input
              className="input"
              value={name}
              maxLength={40}
              placeholder="비워 두면 머리글에 장소만 적습니다"
              onChange={(event) => {
                setName(event.target.value);
                setSaved(false);
              }}
            />
          </label>

          <div className="field">
            <span>
              머리글 장소
              <HelpTip
                text="템플릿 머리글 왼쪽, 조직 이름 옆에 적는 글입니다. 화면 이름은 콘솔에서 화면을 가려내는 이름이라 공공 화면에 어색하면 화면 위치나 직접 입력으로 바꿉니다. 화면 위치는 [화면] 상세의 ‘위치’ 이고, 비어 있는 화면은 장소를 적지 않습니다. 새 판 에이전트부터 따릅니다 — 옛 판 화면은 화면 이름을 적습니다."
                label="머리글 장소 도움말"
              />
            </span>
            <select
              className="input"
              aria-label="머리글 장소"
              value={placeMode}
              onChange={(event) => {
                setPlaceMode(event.target.value as HeaderPlaceMode);
                setSaved(false);
              }}
            >
              <option value="name">화면 이름 — 콘솔에 등록한 이름(기본)</option>
              <option value="location">화면 위치 — 화면 정보의 ‘위치’</option>
              <option value="custom">직접 입력 — 모든 화면에 같은 글</option>
              <option value="none">표시 안 함 — 조직 이름만</option>
            </select>
            {placeMode === 'custom' ? (
              <input
                className="input mt-1.5"
                aria-label="머리글에 적을 글"
                value={placeText}
                maxLength={HEADER_PLACE_MAX}
                placeholder="예: 환경안전팀 · 과천 R&D 캠퍼스"
                onChange={(event) => {
                  setPlaceText(event.target.value);
                  setSaved(false);
                }}
              />
            ) : null}
            {placeMissing ? <small className="text-[12.5px] text-amber-700">머리글에 적을 글을 입력하세요.</small> : null}
          </div>

          <div className="field">
            <span>주 색</span>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="color"
                aria-label="주 색 고르기"
                className="h-9 w-14 cursor-pointer rounded border border-slate-200 bg-white p-0.5"
                value={accent ?? DEFAULT_ACCENT}
                onChange={(event) => {
                  setAccent(event.target.value.toLowerCase());
                  setSaved(false);
                }}
              />
              <span className="mono text-[12.5px] text-slate-600">{accent ?? '기본 파랑'}</span>
              {accent ? (
                <button
                  type="button"
                  className="btn btn-tiny"
                  onClick={() => {
                    setAccent(null);
                    setSaved(false);
                  }}
                >
                  기본 색으로
                </button>
              ) : null}
            </div>
          </div>

          <label className="field">
            <span>로고</span>
            <select
              className="input"
              value={logoId ?? ''}
              onChange={(event) => {
                setLogoId(event.target.value || null);
                setSaved(false);
              }}
            >
              <option value="">로고 없음</option>
              {/* 지금 로고가 첫 페이지 밖에 있어도 고른 값이 사라지지 않게. */}
              {logoId && !logo ? <option value={logoId}>지금 로고</option> : null}
              {(images.data?.items ?? []).map((asset) => (
                <option key={asset.id} value={asset.id}>
                  {asset.title}
                </option>
              ))}
            </select>
            <small className="hint">
              콘텐츠 목록의 이미지에서 고릅니다.
              <HelpTip text="배경이 투명한 가로형 PNG 가 잘 어울립니다. 로고로 쓰는 이미지는 삭제할 수 없습니다." label="로고 도움말" />
            </small>
          </label>
          {logoId && !settings.brand.logo_preview_url && logoId === settings.brand.logo_asset_id ? (
            <p className="text-[12.5px] text-amber-700">
              로고로 고른 이미지가 보관되어 화면에 나가지 않습니다. 다른 이미지를 고르세요.
            </p>
          ) : null}

          <div className="form-actions">
            {saved ? <span className="text-[12.5px] text-emerald-700">저장했습니다</span> : null}
            <button
              type="button"
              className="btn btn-primary"
              disabled={action.pending || !dirty || placeMissing}
              onClick={submit}
            >
              <Save className="h-3.5 w-3.5" aria-hidden="true" /> 저장
            </button>
          </div>

          <div className="field border-t border-slate-200 pt-4">
            <span>사내 글꼴</span>
            {fontAction.error ? <ErrorNote message={fontAction.error} onDismiss={fontAction.clearError} /> : null}
            {settings.brand.font ? (
              <div className="flex flex-wrap items-center gap-2 text-[13px]">
                <span className="mono">{settings.brand.font.filename}</span>
                <span className="text-slate-500">{formatBytes(settings.brand.font.size)}</span>
                <button
                  type="button"
                  className="btn btn-tiny"
                  disabled={fontAction.pending}
                  onClick={() => void fontAction.run(() => client.deleteBrandFont(), accept)}
                >
                  <Trash2 className="h-3 w-3" aria-hidden="true" /> 지우기
                </button>
              </div>
            ) : (
              <p className="hint">올린 글꼴이 없습니다.</p>
            )}
            <div>
              <input
                ref={fileRef}
                type="file"
                accept=".woff2,.woff,.ttf,.otf,font/woff2,font/woff,font/ttf,font/otf"
                hidden
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (file) uploadFont(file);
                }}
              />
              <button type="button" className="btn" disabled={fontAction.pending} onClick={() => fileRef.current?.click()}>
                <Upload className="h-3.5 w-3.5" aria-hidden="true" /> {settings.brand.font ? '다른 파일로 바꾸기' : '글꼴 올리기'}
              </button>
            </div>
            <small className="hint">
              woff2·woff·ttf·otf, 20MB 까지. 배포 권한(화면 게시)을 먼저 확인하세요.
              <HelpTip
                text="템플릿 편집기의 글꼴에서 ‘사내 글꼴’ 을 고른 템플릿이 이 글꼴로 그려집니다. 화면은 처음 한 번 받아 둡니다. 굵기별 파일이 따로 있으면 가장 자주 쓰는 굵기(Bold) 하나를 올리세요."
                label="사내 글꼴 도움말"
              />
            </small>
          </div>
        </div>

        <div className="space-y-2">
          <p className="hint">미리보기 — 저장 전의 값으로 그립니다.</p>
          {/* 견본 화면 — 이름 「로비 화면」, 위치 「본사 1층 로비」. 고른 방식대로 머리글이 바뀐다. */}
          <TemplatePreview
            template={SAMPLE}
            location={headerPlace(draft.place, { name: '로비 화면', location: '본사 1층 로비' })}
            brand={draft}
          />
        </div>
      </div>
    </Panel>
  );
}

const IDLE_OPTIONS: ReadonlyArray<{ mode: IdleMode; label: string; description: string }> = [
  {
    mode: 'message',
    label: '안내 문구',
    description: '"재생할 콘텐츠가 없습니다" 처럼 까닭을 적습니다. 설치·점검 때 화면이 살아 있는지 알기 쉽습니다.',
  },
  { mode: 'black', label: '검은 화면', description: '아무것도 띄우지 않습니다. 로비·공용 공간에 맞습니다.' },
  { mode: 'logo', label: '로고', description: '검은 바탕 가운데 브랜드 로고를 띄웁니다. 로고가 없으면 검은 화면입니다.' },
];

/** 대기 화면과 운영 시간 — 화면이 쉬는 동안의 모습과, 언제 쉬는지. */
function QuietPanel({
  settings,
  save,
  onConflict,
}: {
  settings: TenantSettings;
  save: SaveFn;
  onConflict: () => void;
}): ReactNode {
  const [mode, setMode] = useState<IdleMode>(settings.idle.mode);
  const [enabled, setEnabled] = useState(settings.hours.enabled);
  // 옛 서버는 이 값을 모른다(undefined) — 그때는 고르는 칸을 그리지 않고 보내지도 않는다.
  const panelKnown = settings.hours.panel_off !== undefined;
  const [panelOff, setPanelOff] = useState(settings.hours.panel_off === true);
  const [rules, setRules] = useState<DaypartInput[]>(
    settings.hours.rules.length > 0 ? settings.hours.rules : [{ days: [1, 2, 3, 4, 5], start: '07:00', end: '22:00' }],
  );
  const [saved, setSaved] = useState(false);
  const action = useAction();

  const submit = (): void => {
    setSaved(false);
    void action.run(
      () =>
        reloadOnConflict(
          save({
            idle: { mode },
            // 끈 채로 저장해도 규칙은 남겨 둔다 — 다시 켤 때 처음부터 적지 않게.
            hours: {
              enabled,
              rules: enabled || rules.length > 0 ? rules : [],
              ...(panelKnown ? { panel_off: panelOff } : {}),
            },
          }),
          onConflict,
        ),
      () => setSaved(true),
    );
  };

  return (
    <Panel
      title="대기 화면 · 운영 시간"
      subtitle="편성이 비었을 때의 화면과, 화면이 쉬는 시간을 정합니다."
      help="운영 시간 밖에는 편성·캠페인이 있어도 화면이 검게 쉽니다. 긴급 공지와 바로 보내기는 쉬는 시간에도 나갑니다. 사옥마다 다르면 화면 상세에서 그 화면만 따로 정하거나 늘 켜 둘 수 있습니다."
    >
      <div className="space-y-5 p-4">
        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
        <fieldset className="field">
          <span>편성이 비었을 때</span>
          <div className="grid gap-2 sm:grid-cols-3">
            {IDLE_OPTIONS.map((option) => (
              <label
                key={option.mode}
                className={`flex cursor-pointer flex-col gap-1 rounded-md border p-3 text-[13px] ${
                  mode === option.mode ? 'border-sky-500 bg-sky-50' : 'border-slate-200'
                }`}
              >
                <span className="flex items-center gap-2 font-semibold">
                  <input
                    type="radio"
                    name="idle-mode"
                    checked={mode === option.mode}
                    onChange={() => {
                      setMode(option.mode);
                      setSaved(false);
                    }}
                  />
                  {option.label}
                </span>
                <span className="text-[12px] text-slate-500">{option.description}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="field">
          <label className="flex items-center gap-2 text-[13.5px] font-semibold">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(event) => {
                setEnabled(event.target.checked);
                setSaved(false);
              }}
            />
            운영 시간 정하기
          </label>
          {enabled ? (
            <>
              <p className="hint">아래 시간대에만 편성을 틉니다. 끝이 시작보다 이르면 다음 날까지입니다(예: 18:00~02:00).</p>
              <HoursRulesEditor
                rules={rules}
                onChange={(next) => {
                  setRules(next);
                  setSaved(false);
                }}
              />
            </>
          ) : (
            <p className="hint">정하지 않으면 늘 운영합니다.</p>
          )}
        </div>

        {panelKnown ? (
          <div className="field">
            <label className="flex items-center gap-2 text-[13.5px] font-semibold">
              <input
                type="checkbox"
                checked={panelOff}
                onChange={(event) => {
                  setPanelOff(event.target.checked);
                  setSaved(false);
                }}
              />
              운영 시간 밖에는 패널(TV) 전원 끄기
              <HelpTip
                text="쉬는 동안 화면을 검게 두는 대신 패널을 끄고, 다음 여는 시각 2분 전에 다시 켭니다. 화면 상세에서 그 화면만 정한 운영 시간에도 적용됩니다. 쉬는 시간이 10분이 안 되거나 다음 여는 시각을 모르면 끄지 않습니다. 쉬는 동안 긴급 공지가 오면 윈도우는 곧바로 켜고, 안드로이드는 기기가 잠든 지 몇 분 지나면 다음 깨는 시각에 받습니다(그동안 콘솔에는 오프라인으로 보입니다). 에이전트 0.7.17 부터 — 안드로이드는 루트가 있거나 기기 소유자로 등록된 기기, 윈도우는 모니터 절전 신호로 끕니다. 그 밖의 기기는 지금처럼 검게만 쉽니다."
                label="패널 끄기 도움말"
              />
            </label>
            <p className="hint">
              {panelOff
                ? 'TV 의 HDMI-CEC(기기 연동)가 켜져 있어야 TV 까지 꺼집니다. 꺼져 있으면 신호만 끊겨 TV 가 대기로 들어갑니다.'
                : '끄지 않으면 운영 시간 밖에 검은 화면으로 쉽니다.'}
            </p>
          </div>
        ) : null}

        <div className="form-actions">
          {saved ? <span className="text-[12.5px] text-emerald-700">저장했습니다</span> : null}
          <button
            type="button"
            className="btn btn-primary"
            disabled={action.pending || (enabled && rules.length === 0)}
            onClick={submit}
          >
            <Save className="h-3.5 w-3.5" aria-hidden="true" /> 저장
          </button>
        </div>
      </div>
    </Panel>
  );
}

/** 제목을 얹는 자리 — 미리보기 화면의 어디에 놓이는가와 함께. */
const CAPTION_SPOTS: ReadonlyArray<{ position: CaptionPosition; label: string; place: string }> = [
  { position: 'top-left', label: '왼쪽 위', place: 'left-2 top-2' },
  { position: 'top-center', label: '가운데 위', place: 'left-1/2 top-2 -translate-x-1/2' },
  { position: 'top-right', label: '오른쪽 위', place: 'right-2 top-2' },
  { position: 'bottom-left', label: '왼쪽 아래', place: 'bottom-2 left-2' },
  { position: 'bottom-center', label: '가운데 아래', place: 'bottom-2 left-1/2 -translate-x-1/2' },
  { position: 'bottom-right', label: '오른쪽 아래', place: 'bottom-2 right-2' },
];

const CAPTION_OVERFLOW_OPTIONS: ReadonlyArray<{ overflow: CaptionOverflow; label: string; description: string }> = [
  { overflow: 'ellipsis', label: '말줄임', description: '한 줄로 두고 넘치는 끝을 … 로 줄입니다.' },
  { overflow: 'marquee', label: '흐르기', description: '한 줄로 두고 넘치면 글자가 옆으로 흘러 끝까지 보입니다.' },
  { overflow: 'wrap', label: '두 줄', description: '두 줄까지 꺾고, 그래도 넘치면 끝을 … 로 줄입니다.' },
];

/**
 * 재생 중 제목 표시(2026-10-28) — 화면이 지금 틀고 있는 콘텐츠의 제목을 얹는다. 켬, 자리(여섯 곳), 긴 제목을 다루는 법
 * (말줄임·흐르기·두 줄). 제목은 콘텐츠 목록의 제목이다. 에이전트 1.0.2 부터 그린다 — 옛 화면은 이 설정을 모르고 지나친다.
 */
function CaptionPanel({
  settings,
  save,
  onConflict,
}: {
  settings: TenantSettings;
  save: SaveFn;
  onConflict: () => void;
}): ReactNode {
  const initial = settings.caption ?? { enabled: false, position: 'bottom-left', overflow: 'ellipsis' };
  const [enabled, setEnabled] = useState(initial.enabled);
  const [position, setPosition] = useState<CaptionPosition>(initial.position);
  const [overflow, setOverflow] = useState<CaptionOverflow>(initial.overflow);
  const [saved, setSaved] = useState(false);
  const action = useAction();
  const spot = CAPTION_SPOTS.find((entry) => entry.position === position) ?? CAPTION_SPOTS[3]!;

  const submit = (): void => {
    setSaved(false);
    void action.run(
      () => reloadOnConflict(save({ caption: { enabled, position, overflow } }), onConflict),
      () => setSaved(true),
    );
  };

  return (
    <Panel
      title="재생 중 제목 표시"
      subtitle={
        settings.caption?.enabled
          ? `켜짐 · ${CAPTION_SPOTS.find((entry) => entry.position === settings.caption?.position)?.label ?? ''} · ${
              CAPTION_OVERFLOW_OPTIONS.find((entry) => entry.overflow === settings.caption?.overflow)?.label ?? ''
            }`
          : '꺼짐 · 화면에 지금 나가는 콘텐츠의 제목을 얹습니다'
      }
      help="켜면 모든 화면이 지금 틀고 있는 콘텐츠의 제목(콘텐츠 목록의 제목)을 고른 자리에 띄웁니다. 제목을 바꾸면 화면에도 곧 바뀝니다. 에이전트 1.0.2 부터 나옵니다 — 그 전 버전의 화면은 이 설정을 모르고 지나칩니다."
    >
      <div className="space-y-5 p-4">
        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
        <label className="flex items-center gap-2 text-[13.5px] font-semibold">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(event) => {
              setEnabled(event.target.checked);
              setSaved(false);
            }}
          />
          재생 중인 콘텐츠의 제목을 화면에 띄우기
        </label>

        <fieldset className="field" disabled={!enabled}>
          <span>자리</span>
          {/* 미리보기 화면 — 여섯 자리를 눌러 고른다. 고른 자리에 제목 상자가 놓인다. */}
          <div
            role="radiogroup"
            aria-label="제목 자리"
            className={`relative aspect-video w-full max-w-md overflow-hidden rounded-md border border-slate-300 bg-slate-800 ${
              enabled ? '' : 'opacity-50'
            }`}
          >
            {CAPTION_SPOTS.map((entry) => {
              const chosen = entry.position === position;
              return (
                <button
                  key={entry.position}
                  type="button"
                  role="radio"
                  aria-checked={chosen}
                  aria-label={entry.label}
                  title={entry.label}
                  className={`absolute ${entry.place} max-w-[60%] truncate rounded px-2 py-1 text-[11.5px] font-semibold ${
                    chosen ? 'bg-black/75 text-white ring-2 ring-sky-400' : 'border border-dashed border-slate-500 text-slate-400'
                  }`}
                  onClick={() => {
                    setPosition(entry.position);
                    setSaved(false);
                  }}
                >
                  {chosen ? '콘텐츠 제목' : entry.label}
                </button>
              );
            })}
          </div>
          <small className="hint">고른 자리: {spot.label}. 미리보기의 자리를 눌러 바꿉니다.</small>
        </fieldset>

        <fieldset className="field" disabled={!enabled}>
          <span>긴 제목</span>
          <div className="grid gap-2 sm:grid-cols-3">
            {CAPTION_OVERFLOW_OPTIONS.map((option) => (
              <label
                key={option.overflow}
                className={`flex cursor-pointer flex-col gap-1 rounded-md border p-3 text-[13px] ${
                  overflow === option.overflow ? 'border-sky-500 bg-sky-50' : 'border-slate-200'
                } ${enabled ? '' : 'opacity-50'}`}
              >
                <span className="flex items-center gap-2 font-semibold">
                  <input
                    type="radio"
                    name="caption-overflow"
                    checked={overflow === option.overflow}
                    onChange={() => {
                      setOverflow(option.overflow);
                      setSaved(false);
                    }}
                  />
                  {option.label}
                </span>
                <span className="text-[12px] text-slate-500">{option.description}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="form-actions">
          {saved ? <span className="text-[12.5px] text-emerald-700">저장했습니다</span> : null}
          <button type="button" className="btn btn-primary" disabled={action.pending} onClick={submit}>
            <Save className="h-3.5 w-3.5" aria-hidden="true" /> 저장
          </button>
        </div>
      </div>
    </Panel>
  );
}

/**
 * 올린 HTML 의 바깥 연결 막기(2026-11-03) — 켜면 화면·미리보기가 올린 HTML 을 문서 안의 것만으로 띄운다(`HTML_CSP_OFFLINE`).
 * 인터넷이 없는 현장은 잃는 것이 없고, 승인한 뒤 바깥 파일이 바뀌어 화면이 바뀌는 일이 없다. CDN 의 글꼴·스크립트를 부르는
 * HTML 은 그 부분이 빠진다 — 어느 HTML 이 그런지는 콘텐츠 상세가 알린다.
 */
function HtmlOfflinePanel({
  settings,
  save,
  onConflict,
}: {
  settings: TenantSettings;
  save: SaveFn;
  onConflict: () => void;
}): ReactNode {
  const [offline, setOffline] = useState(settings.html?.offline ?? false);
  const [saved, setSaved] = useState(false);
  const action = useAction();
  const { reload } = useBrand();

  return (
    <Panel
      title="올린 HTML 의 바깥 연결"
      subtitle={settings.html?.offline ? '막음 · HTML 은 파일 안의 것만으로 나갑니다' : '허용 · HTML 이 https 주소에서 글꼴·그림·스크립트를 불러옵니다'}
      help="올린 HTML 이 CDN 같은 바깥 주소에서 불러오는 부분은 인터넷이 있어야 나오고, 그 주소의 파일이 바뀌면 콘솔에서 승인한 뒤에도 화면이 바뀝니다. 막으면 HTML 은 파일 안의 것만으로 나갑니다 — 인터넷이 없는 현장은 잃는 것이 없습니다. 바깥에서 불러오는 HTML 은 콘텐츠 상세에 그 주소가 나옵니다. 새 판 에이전트부터 따릅니다(옛 판 화면은 예전처럼 엽니다)."
    >
      <div className="space-y-4 p-4">
        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
        <label className="flex items-start gap-2 text-[13.5px]">
          <input
            type="checkbox"
            className="mt-1"
            checked={offline}
            onChange={(event) => {
              setOffline(event.target.checked);
              setSaved(false);
            }}
          />
          <span>
            <span className="font-semibold">바깥 연결 막기</span>
            <span className="block text-[12.5px] text-slate-500">
              화면과 콘솔 미리보기가 올린 HTML 을 그 파일 안의 것(글·그림·스크립트)만으로 띄웁니다. 바깥 주소의 글꼴·그림·스크립트는
              빠집니다.
            </span>
          </span>
        </label>
        <div className="form-actions">
          {saved ? <span className="text-[12.5px] text-emerald-700">저장했습니다</span> : null}
          <button
            type="button"
            className="btn btn-primary"
            disabled={action.pending}
            onClick={() => {
              setSaved(false);
              void action.run(
                () => reloadOnConflict(save({ html: { offline } }), onConflict),
                () => {
                  setSaved(true);
                  // 콘텐츠 상세의 바깥 주소 알림이 새 설정으로 말하게.
                  reload();
                },
              );
            }}
          >
            <Save className="h-3.5 w-3.5" aria-hidden="true" /> 저장
          </button>
        </div>
      </div>
    </Panel>
  );
}

/**
 * 새 화면 그룹 — 등록할 때 그룹을 고르지 않은 화면이 드는 그룹(2026-10-28). 그 그룹의 편성을 물려받아 등록하자마자
 * 튼다. 납품 패키지가 기본 콘텐츠를 심으며 「기본 콘텐츠」 그룹으로 정해 둔다 — 고객사가 바꾸거나 끈다.
 */
function NewScreenPanel({
  client,
  settings,
  save,
  onConflict,
}: {
  client: SignageOneClient;
  settings: TenantSettings;
  save: SaveFn;
  onConflict: () => void;
}): ReactNode {
  const groups = useAsync(() => client.listGroups(), [client]);
  const [groupId, setGroupId] = useState<string | null>(settings.new_display_group_id ?? null);
  const [saved, setSaved] = useState(false);
  const action = useAction();
  const items = groups.data?.items ?? [];
  const current = items.find((group) => group.id === groupId);

  const submit = (): void => {
    setSaved(false);
    void action.run(
      () => reloadOnConflict(save({ new_display_group_id: groupId }), onConflict),
      () => setSaved(true),
    );
  };

  return (
    <Panel
      title="새 화면 그룹"
      subtitle={current ? `「${current.name}」 · 등록하자마자 이 그룹의 편성을 틉니다` : '없음 · 새 화면은 그룹 없이 들어 대기 화면을 띄웁니다'}
      help="화면을 등록할 때 그룹을 고르지 않으면 이 그룹에 듭니다. 그 그룹에 배정한 플레이리스트·스케줄을 곧바로 틉니다. 화면을 다른 그룹으로 옮기거나 화면에 직접 배정하면 그것이 이깁니다."
    >
      <div className="space-y-3 p-4">
        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
        {groups.error ? <ErrorNote message={groups.error} /> : null}
        <label className="field">
          <span>그룹</span>
          <select
            className="input"
            value={groupId ?? ''}
            onChange={(event) => {
              setGroupId(event.target.value || null);
              setSaved(false);
            }}
          >
            <option value="">그룹 없이</option>
            {items.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
        </label>
        <div className="form-actions">
          {saved ? <span className="text-[12.5px] text-emerald-700">저장했습니다</span> : null}
          <button type="button" className="btn btn-primary" disabled={action.pending} onClick={submit}>
            <Save className="h-3.5 w-3.5" aria-hidden="true" /> 저장
          </button>
        </div>
      </div>
    </Panel>
  );
}

function AllowlistPanel({
  settings,
  save,
  onConflict,
}: {
  settings: TenantSettings;
  save: SaveFn;
  onConflict: () => void;
}): ReactNode {
  const [text, setText] = useState(settings.web_allowlist.join('\n'));
  const [saved, setSaved] = useState(false);
  const action = useAction();

  const submit = (): void => {
    setSaved(false);
    void action.run(
      () => reloadOnConflict(save({ web_allowlist: parseAllowlistText(text) }), onConflict),
      (next) => {
        setText(next.web_allowlist.join('\n'));
        setSaved(true);
      },
    );
  };

  return (
    /* 한 번 정하면 거의 고치지 않는 설정 — 접을 수 있고, 접으면 머리에 몇 개인지 남긴다. */
    <Panel
      title="웹 콘텐츠 허용 주소"
      subtitle={`${settings.web_allowlist.length > 0 ? `${settings.web_allowlist.length}개` : '없음'} · 사내 포털·대시보드 같은 사내망 페이지를 화면에 띄울 때`}
      collapsible
      help={
        <>
          웹 콘텐츠는 기본으로 공개 인터넷 주소만 받습니다. 사설 IP(10.x·192.168.x 등)·사내 도메인(.local·.intranet
          등)·점 없는 이름은 막혀 있습니다 — 사내 관리 페이지가 실수로 로비 화면에 뜨지 않게 하려는 것입니다. 여기
          적은 주소만 그 예외가 됩니다. 루프백·링크로컬(169.254.x)은 적어도 열리지 않습니다.
        </>
      }
    >
      <div className="space-y-3 p-4">
        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
        <label className="field">
          <span>한 줄에 하나</span>
          <textarea
            className="input mono"
            rows={6}
            value={text}
            spellCheck={false}
            placeholder={'portal.example.co.kr\n*.intra.example.co.kr\n10.20.0.0/16'}
            onChange={(event) => {
              setText(event.target.value);
              setSaved(false);
            }}
          />
        </label>
        {/* 적는 법은 처음 한 번 읽으면 된다 — [작성 예] 뒤로. */}
        <Disclosure title="작성 예" summary="호스트 · *.도메인 · IP·대역">
          <ul className="hint list-disc pl-5">
            <li>
              <span className="mono">portal.example.co.kr</span> — 그 호스트 하나. 점 없는 사내 이름(
              <span className="mono">intranet</span>)도 됩니다.
            </li>
            <li>
              <span className="mono">*.intra.example.co.kr</span> — 그 아래 이름 전부
            </li>
            <li>
              <span className="mono">10.20.30.40</span> · <span className="mono">10.20.0.0/16</span> — IP 하나 또는 대역(/8
              이상)
            </li>
            <li>주소를 통째로 붙여 넣어도 호스트만 읽습니다. 포트는 보지 않습니다.</li>
          </ul>
          <p className="hint">
            목록에서 지워도 이미 등록된 웹 콘텐츠는 그대로 나갑니다. 내리려면 그 콘텐츠를 보관하세요.
          </p>
        </Disclosure>
        <div className="form-actions">
          {saved ? <span className="text-[12.5px] text-emerald-700">저장했습니다</span> : null}
          <button type="button" className="btn btn-primary" disabled={action.pending} onClick={submit}>
            <Save className="h-3.5 w-3.5" aria-hidden="true" /> 저장
          </button>
        </div>
      </div>
    </Panel>
  );
}

/**
 * 통합 관제 연결 — **이 서버를 다른 서버의 통합 관제에 묶을 때** 그쪽에 넣을 토큰을 만든다.
 *
 * 처음에는 공급사의 시스템 화면에 있었다. 여러 서버를 둔 고객사가 공급사를 부르지 않고 스스로 묶게 고객사
 * 관리자의 자리로 옮겼다(2026-10-26 사용자 지시). 서버가 이름·역할(통합 관제 · 관리자)을 정해 만들고, 이
 * 목록에는 그 토큰만 온다 — 다른 연동의 토큰은 보이지도 폐기되지도 않는다. 관리자 역할인 것은 원격 명령
 * (재부팅·앱 종료·전원 종료)에 화면 관리 권한이 들기 때문이다. 평문은 만들 때 한 번만 보인다(서버가 해시만
 * 둔다). 잃으면 폐기하고 새로 만든다.
 */
function HubLinkPanel({ client }: { client: SignageOneClient }): ReactNode {
  const tokens = useAsync(() => client.listHubLinkTokens(), [client]);
  const [issued, setIssued] = useState<HubLinkTokenIssued | null>(null);
  const action = useAction();
  const items = tokens.data?.items ?? [];

  return (
    <Panel
      title="통합 관제 연결"
      subtitle={`${tokens.data ? (items.length > 0 ? `토큰 ${items.length}개` : '묶이지 않음') : '확인 중'} · 다른 서버의 통합 관제가 이 서버의 화면을 모아 볼 때`}
      collapsible
      help="여러 서버를 한 곳에서 볼 때 씁니다. 여기서 만든 토큰과 이 서버의 주소(망이 다르면 Sentinel Grid 주소)를 모아 보는 서버의 통합 관제 ▸ 서버 추가에 넣으세요. 모아 보는 서버 쪽에서는 이 서버의 화면 현황을 보고 재부팅·앱 종료·전원 종료를 보낼 수 있습니다."
    >
      <div className="space-y-3 p-4">
        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
        {tokens.error ? <ErrorNote message={tokens.error} /> : null}
        {issued ? (
          <div className="space-y-2">
            <p className="text-[13px] text-slate-700">
              새 토큰입니다. <strong>창을 떠나면 다시 볼 수 없습니다</strong> — 지금 복사해 통합 관제에 넣으세요.
            </p>
            <CommandBox command={issued.token} />
          </div>
        ) : null}
        {items.length > 0 ? (
          <ul className="divide-y divide-slate-100 text-[13px]">
            {items.map((token) => (
              <li key={token.id} className="flex items-center gap-3 py-2">
                <KeyRound className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium text-slate-800">{token.name} 토큰</span>
                  <span className="block text-[12px] text-slate-500">
                    발급 <RelTime iso={token.created_at} /> · 마지막 사용{' '}
                    {token.last_used_at ? <RelTime iso={token.last_used_at} /> : '없음'}
                  </span>
                </span>
                <IconButton
                  icon={Trash2}
                  label="폐기"
                  subject={`${token.name} 토큰`}
                  danger
                  disabled={action.pending}
                  onClick={async () => {
                    const ok = await confirmDialog({
                      title: '통합 관제 토큰 폐기',
                      body: '이 토큰으로 이 서버를 모아 보던 통합 관제가 곧바로 연결을 잃습니다(토큰 거절).',
                      confirmLabel: '폐기',
                      danger: true,
                    });
                    if (ok) await action.run(() => client.revokeHubLinkToken(token.id), () => tokens.reload(), '폐기했습니다');
                  }}
                />
              </li>
            ))}
          </ul>
        ) : tokens.data ? (
          <p className="hint">아직 통합 관제에 묶이지 않았습니다.</p>
        ) : null}
        <button
          type="button"
          className="btn btn-primary"
          disabled={action.pending}
          onClick={() =>
            void action.run(
              () => client.createHubLinkToken(),
              (created) => {
                setIssued(created);
                tokens.reload();
              },
            )
          }
        >
          <KeyRound className="h-3.5 w-3.5" aria-hidden="true" /> 통합 관제 토큰 만들기
        </button>
        <p className="hint">
          이 서버 주소: <span className="mono">{window.location.origin}</span> (망이 다르면 Sentinel Grid 주소 — 서버 앞에서{' '}
          <span className="mono">signageonectl grid status</span> 가 보여 줍니다)
        </p>
      </div>
    </Panel>
  );
}
